/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import ThumbnailComponentV2 from './ThumbnailComponentV2';
import type {
  CarouselItem,
  CarouselItemTypes,
} from '@/components/Carousel/ThumbnailComponent/types';
import { eventConstants } from '@/constants/event';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useDebouncedResizeObserver } from '../../utils/useDebouncedResizeObserver';
import React, { useRef, useState, Suspense, useCallback, useMemo } from 'react';
import { useGcpProductVideos } from '../../api/getGcpProductVideos';
import { CarouselComponentV2 } from './CarouselComponent/CarouselComponentV2';
import styles from './styles.module.scss';
import { map360CarouselItem } from './utils/map360CarouselItem';
import { type ProductModel } from '@/api/types/products-types';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { useFeatureFlag } from '@/features/globalConfig';
import dynamic from 'next/dynamic';
import { YMMESpecificInstallVideoBannerV2 } from '@/components/ProductDetail/ProductAvailabilityInfo/YMMESpecificInstallVideoBanner/YMMESpecificInstallVideoBannerV2';
import { useSylvaniaVideos } from '@/features/product/api/getSylvaniaVideos';
import { trackImageInteractionPDP } from '../../utils/analytics/trackImageInteractionPDP';
import { ConfigurableSkuBadge } from '@/features/configurableSku/components/ConfigurableSkuBadge';
import { useGetGcp360Image } from '../../api/getGcp360Image';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useContentStackDefaultPDPContent } from '../../api/getContentStackDefaultProductContent';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { pageTypes } from '@/utils/analytics/legacyUtils/pageTypes';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { analyticsDynamicPageName } from '@/utils/analytics/legacyUtils/analyticsDynamicPageName';
import VehicleSpecificImagesBanner from '@/components/ProductDetail/ProductAvailabilityInfo/VehicleSpecificImagesBanner/VehicleSpecificImagesBanner';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { getImagesWithDefault } from '../../utils/productImageUtils';
import { useAcesSylvaniaVideos } from '@/features/product/api/getAcesSylvaniaVideo';
import { countryCodes } from '@/constants/locale';
import { View } from '@az/starc-ui';
import { useGetVehicleInstallGuide } from '../../api/getVehicleInstallGuide';
import { useCatalogConfigs } from '@/features/common/api/getCatalogConfig';
import { useHeaderData } from '@/features/header/api/getHeader';
import VehicleInstallGuideVideoModalBanner from '@/components/ProductDetail/ProductAvailabilityInfo/VehicleInstallGuideVideoModalBanner/VehicleInstallGuideVideoModalBanner';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

const LazyProductImageModal = dynamic(
  () => import('./ProductImageModal').then((mod) => mod.ProductImageModal),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

const LazyEnlargedMediaComponent = dynamic(() =>
  import('./EnlargedMediaComponent').then((mod) => mod.EnlargedMediaComponent),
);

type Props = {
  data: ProductModel;
  labels: Record<string, string>;
};

const labelMap = {
  label_CategoryPage_body_ImageNotAvailable: 'label_CategoryPage_body_ImageNotAvailable',
  hoverToZoomLabel: 'label_hover_over_image_to_zoom',
  tapToZoomLabel: 'Label_tap_image_to_zoom',
  tapImage360Label: 'label_tap_image_to_view_360',
  dragImageLabel: 'label_drag_image_to_view_all_slides',
};

// TODO: mobile video thumbnail issue
// TODO: number of thumbnails is too few for tablet (e.g. should be 6 but only 4 for https://localhost.autozone.com/p/duralast-gold-battery-35-dlg/115788)
// TODO: thumbnails can change order when breakpoint changes if video/360 thumbnail also exist; might be related to the above comment
export const ProductCarouselV2 = (props: Props) => {
  const {
    itemDescription,
    itemMediaUrl,
    itemId,
    originalPartTypeId,
    location,
    cmsContentPath,
    taxonomyUrl,
    partNumber,
    partGroupId,
    partTypeName,
  } = props.data;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const labels = useLabels(labelMap);
  const locale = useLocale();
  const country = getCountryFromLocale(locale);
  const { acesEnabled } = useAcesFlags();
  const initializedImages = useRef(0);
  const [modalTab, setModalTab] = useState('images');
  const [pinchToZoomImagesInitialized, setPinchToZoomImagesInitialized] = useState(false);
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const { hoverToZoomLabel, tapToZoomLabel, tapImage360Label, dragImageLabel } = labels;
  const [selectedModalThumbnailIndex, setSelectedModalThumbnailIndex] = useState(0);
  const [selectedPDPThumbnailIndex, setSelectedPDPThumbnailIndex] = useState(0);
  const [aprimoVideoTag, setAprimoVideoTag] = useState<HTMLVideoElement | null>(null);
  const productImages = useMemo(
    () => (itemMediaUrl?.productImages as string[]) || [],
    [itemMediaUrl?.productImages],
  );
  const defaultImage = itemMediaUrl?.defaultItemImageUrl;
  const productImageRefObject = useRef<HTMLDivElement>(null);
  const [enlargedMediaContainerWidth, setEnlargedMediaContainerWidth] = useState(0);
  const { data } = useHeaderData();
  const vehicleDataHeader = data?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsNames;
  const vehicleFuelTypeId =
    data?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsIds?.fuelTypeId;

  // Callback ref to measure initial width on mount
  const productImageRefCallback = useCallback((element: HTMLDivElement | null) => {
    productImageRefObject.current = element;

    if (element) {
      const width = element.getBoundingClientRect().width;
      setEnlargedMediaContainerWidth(width);
    }
  }, []);

  const imagesWithDefault = useMemo(
    () => (acesEnabled ? getImagesWithDefault(productImages, defaultImage) : []),
    [acesEnabled, productImages, defaultImage],
  );

  const enlargedImages = useMemo(
    () => (acesEnabled ? imagesWithDefault : itemMediaUrl?.enlargedItemImageUrls || []),
    [acesEnabled, imagesWithDefault, itemMediaUrl?.enlargedItemImageUrls],
  );

  const imageThumbnails = useMemo(
    () => (acesEnabled ? imagesWithDefault : itemMediaUrl?.thumbnailItemImageUrls || []),
    [acesEnabled, imagesWithDefault, itemMediaUrl?.thumbnailItemImageUrls],
  );

  const getThumbnailImageURL = useCallback((imageURL: string) => {
    if (imageURL) {
      return `${imageURL}`;
    }

    return imageURL;
  }, []);

  const getMediumImageURL = useCallback((imageURL: string | undefined) => {
    if (
      imageURL &&
      /^https:\/\/(st-|pr-|qa-|dv-)?contentinfo\.autozone\.com\/znetcs\/product-info\/([^/]+\/){4}image\/\d+\/$/.test(
        imageURL,
      )
    ) {
      return imageURL.replace(/\/\d+\/$/, '/8/');
    }

    return imageURL;
  }, []);

  const mapToImageObject = useCallback(
    (enlargedMediaUrl: string, index: number): CarouselItem => {
      return {
        alt: itemDescription,
        enlargedMediaUrl,
        mediumImageURL: index === 0 ? getMediumImageURL(enlargedMediaUrl) : undefined,
        thumbnail: acesEnabled ? getThumbnailImageURL(enlargedMediaUrl) : imageThumbnails[index],
        type: 'image',
      };
    },
    [itemDescription, getMediumImageURL, acesEnabled, getThumbnailImageURL, imageThumbnails],
  );

  const imageData = useMemo(
    () => enlargedImages?.map(mapToImageObject) ?? [],
    [enlargedImages, mapToImageObject],
  );

  const isVehicleSpecificImagesBannerEnabled = useForteDecisionFlagWithTrueDefault(
    'vehicleSpecificImagesBannerEnabled',
  );

  const isVehicleInstallGuideVideoEnabled = useForteDecisionFlagWithTrueDefault(
    'vehicleInstallGuideVideoEnabled',
  );

  const isUsOrMxSite = locale === countryCodes.us || locale === countryCodes.mx;

  const hasValidItemId = itemId !== null && itemId !== undefined && String(itemId).length > 0;

  const { data: threeSixtyImage } = useGetGcp360Image({
    country,
    skuIds: itemId,
    enabled: isUsOrMxSite && hasValidItemId,
  });

  const threeSixtyImageData = Array.isArray(threeSixtyImage) ? threeSixtyImage[0] : threeSixtyImage;

  const threeSixtyUrls = threeSixtyImageData?.urls ?? [];

  const urls =
    threeSixtyUrls.length > 0
      ? threeSixtyImageData?.thumbnail
        ? [threeSixtyImageData.thumbnail, ...threeSixtyUrls]
        : threeSixtyUrls
      : undefined;

  const threeSixtyImageThumbnails = map360CarouselItem(
    locale,
    threeSixtyImageData?.thumbnail
      ? {
          thumbnail: threeSixtyImageData.thumbnail,
        }
      : undefined,
    itemDescription,
  );

  const {
    data: productDetailsData,
    isSuccess: isProductDetailsSuccess,
    isFetching: isProductDetailsFetching,
  } = useProductDetails();
  const isProductDetailsReady = isProductDetailsSuccess && !isProductDetailsFetching;

  const taxonomyPath = cmsContentPath ?? taxonomyUrl;
  const { data: defaultPDPContent } = useContentStackDefaultPDPContent({
    enabled: isProductDetailsReady,
    taxonomyPath,
  });
  const partTypeHasVehicleImages =
    defaultPDPContent?.partTypesWithVehicleImages?.includes(originalPartTypeId ?? '') ?? false;
  const vehicleData = usePreferredVehicle();
  const isProductVehicleImageEnabled =
    useFeatureFlag('IS_PRODUCT_VEHICLE_IMAGE_ENABLED') === 'true';

  const { data: catalogConfigsData } = useCatalogConfigs({
    itemId: partGroupId ?? '',
    itemType: 'product',
  });

  const installationLocation = catalogConfigsData?.configs?.installationProcedureName;

  const { data: vehicleInstallGuideData } = useGetVehicleInstallGuide({
    country,
    year: vehicleData?.year ?? '',
    makeName: vehicleDataHeader?.makeName ?? '',
    modelName: vehicleDataHeader?.modelName ?? '',
    engineName: vehicleDataHeader?.engineName ?? '',
    fuelTypeId: vehicleFuelTypeId ?? 0,
    location: installationLocation ?? '',
    enabled:
      isProductVehicleImageEnabled &&
      !!props.data.vehicleFitFlag &&
      !!vehicleDataHeader?.makeName &&
      !!vehicleDataHeader?.modelName &&
      !!vehicleDataHeader?.engineName &&
      !!installationLocation,
  });
  const hasInstallGuideVideos = (vehicleInstallGuideData?.videos?.length ?? 0) > 0;

  const vehicleInstallationGuideVideos = vehicleInstallGuideData?.videos ?? [];

  const productVehicleImage = hasInstallGuideVideos
    ? undefined
    : vehicleInstallGuideData
      ? { images: vehicleInstallGuideData.images ?? [] }
      : undefined;

  const installVideosEnabled = useFeatureFlag('IS_YMME_INSTALL_VIDEOS_ENABLED') === 'true';
  const { data: partsProSylvaniaData } = useSylvaniaVideos({
    enabled: installVideosEnabled && !acesEnabled,
    productId: props.data.itemId,
  });

  const { data: acesSylvaniaData } = useAcesSylvaniaVideos({
    enabled: installVideosEnabled && acesEnabled,
    skuId: props.data.itemId,
  });

  const sylvaniaData = acesEnabled ? acesSylvaniaData : partsProSylvaniaData;

  const installationGuideEnabled = Boolean(installVideosEnabled && !!sylvaniaData);
  const vehicleInstallGuideEnabled =
    isVehicleInstallGuideVideoEnabled && hasInstallGuideVideos && !!props.data.vehicleFitFlag;

  const gcpProductsVideosResult = useGcpProductVideos({
    country,
    skuId: itemId,
    enabled: true,
  });

  const videoThumbnails: CarouselItem[] = useMemo(
    () =>
      gcpProductsVideosResult.data?.map(({ thumbnailURL, videoURL, title }) => ({
        thumbnail: thumbnailURL,
        enlargedMediaUrl: videoURL,
        title,
        type: 'video' as const,
      })) ?? [],
    [gcpProductsVideosResult.data],
  );

  const videoThumbnailsExist = !!videoThumbnails.length;
  const allThumbnails = useMemo(
    () => [...imageData, ...threeSixtyImageThumbnails, ...videoThumbnails],
    [imageData, threeSixtyImageThumbnails, videoThumbnails],
  );

  const maxVisibleThumbnails = useMemo(() => {
    if (isMobile) {
      return allThumbnails.length; // Show all thumbnails on mobile via scroll
    }
    // Desktop/tablet: calculate based on container width
    const gap = 12;
    const thumbnailWidth = 60;
    return Math.max(1, Math.floor(enlargedMediaContainerWidth / (thumbnailWidth + gap)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, enlargedMediaContainerWidth]);

  /**
   * MOBILE: Shows all thumbnails in horizontal scroll (no slicing, no reordering, no "See More")
   * DESKTOP/TABLET: Dynamically calculates max visible based on container width and prioritizes
   * video/360 thumbnails to appear before "See More" overlay
   */
  const visibleThumbnails = useMemo(() => {
    if (isMobile) {
      return allThumbnails;
    }

    const hasVideo = videoThumbnails.length > 0;
    const has360 = threeSixtyImageThumbnails.length > 0;

    if (!hasVideo && !has360) {
      return allThumbnails.slice(0, maxVisibleThumbnails);
    }

    if (allThumbnails.length <= maxVisibleThumbnails) {
      return allThumbnails;
    }

    const additionalThumbnails = [
      ...(hasVideo ? [videoThumbnails[0]] : []),
      ...threeSixtyImageThumbnails,
    ];
    // SEE MORE overlays the last thumbnail, doesn't add a slot
    const imageCutOff = maxVisibleThumbnails - additionalThumbnails.length;
    const shouldReorder = imageData.length > imageCutOff && additionalThumbnails.length > 0;

    if (!shouldReorder) {
      return allThumbnails.slice(0, maxVisibleThumbnails);
    }

    // Build reordered list from imageData only (to avoid duplicating 360/video thumbnails)
    const reordered = [
      ...imageData.slice(0, Math.min(imageCutOff, imageData.length)),
      ...additionalThumbnails,
    ];

    return reordered.slice(0, maxVisibleThumbnails);
  }, [
    isMobile,
    allThumbnails,
    maxVisibleThumbnails,
    videoThumbnails,
    threeSixtyImageThumbnails,
    imageData,
  ]);

  const showSeeMore = !isMobile && allThumbnails.length > maxVisibleThumbnails;

  const onPinchToZoomImageInit = () => {
    initializedImages.current = initializedImages.current + 1;
    if (initializedImages.current === enlargedImages.length) {
      //avoid jumpy centering on mount of pinch to zoom images
      setTimeout(() => {
        setPinchToZoomImagesInitialized(true);
      }, 100);
    }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [enlargedMediaData, setEnlargedMediaData] = useState(
    allThumbnails[0] ? allThumbnails[0] : null,
  );
  const enlargedMedia = enlargedMediaData ?? (allThumbnails[0] ? allThumbnails[0] : undefined);
  const enlargedMediaIsImage = enlargedMedia?.type === 'image';
  const enlargedMediaIs360 = enlargedMedia?.type === 'spin360';
  const imageLabel = isDesktop ? hoverToZoomLabel : tapToZoomLabel;
  const image360Label = !isMobile ? dragImageLabel : tapImage360Label;
  const imageCaption = enlargedMediaIsImage ? imageLabel : enlargedMediaIs360 ? image360Label : '';

  const findThumbnailIndex = (thumbnails: CarouselItem[], thumbnailSrc?: string) => {
    return thumbnails.findIndex(
      (thumbnailObject: CarouselItem) => thumbnailObject.thumbnail === thumbnailSrc,
    );
  };

  const pdpBreadcrumb = productDetailsData?.product?.breadCrumbs;
  const pageTypeData = 'pdp';
  const pageName = analyticsDynamicPageName(pdpBreadcrumb, pageTypeData);
  const handleModalTabChange = (tab: string) => {
    setModalTab(tab);
    let selectedCarouselItem;
    if (tab === '360 Image') {
      selectedCarouselItem = threeSixtyImageThumbnails[0];
    } else if (tab === 'Videos') {
      selectedCarouselItem = videoThumbnails[0];
    } else if (tab === 'Port Locator') {
      const productVehicleImageThumbnail =
        productVehicleImage?.images.map(({ imageURL }, index) =>
          mapToImageObject(imageURL, index),
        ) ?? [];
      selectedCarouselItem = productVehicleImageThumbnail[0];
      if (isProductVehicleImageEnabled && !!vehicleData && partTypeHasVehicleImages) {
        clickTrack({
          customLinkName: 'Vehcile Specific Image View',
          eventType: 'vehicleSpecificImageView',
          pageName: `az:${pageName}-${partNumber}`,
          pageType: pageTypes.pdp,
          siteSection: 'catalog',
          productId: partNumber,
        });
      }
    } else {
      selectedCarouselItem = imageData[0];
    }
    showExpandedImageModal(0, selectedCarouselItem);
  };

  const selectModalTab = (type?: CarouselItemTypes) => {
    if (type === 'spin360') {
      setModalTab('360 Image');
      return;
    } else if (type === 'video') {
      setModalTab('Videos');
      return;
    }
    setModalTab('Images');
  };

  const showExpandedImageModal = (index: number, carouselItem: CarouselItem) => {
    const { thumbnail } = carouselItem;
    const pdpIndex: number = findThumbnailIndex(allThumbnails, thumbnail);
    setSelectedModalThumbnailIndex(index);
    setSelectedPDPThumbnailIndex(pdpIndex);
    setEnlargedMediaData(carouselItem);
  };

  const showExpandedImagePDP = useCallback(
    (index: number, carouselItem: CarouselItem) => {
      const { type, thumbnail } = carouselItem;
      const dataSet =
        type === 'image'
          ? imageData
          : type === 'spin360'
            ? threeSixtyImageThumbnails
            : videoThumbnails;
      const modalIndex: number = findThumbnailIndex(dataSet, thumbnail);
      selectModalTab(carouselItem.type);
      setSelectedPDPThumbnailIndex(index);
      setSelectedModalThumbnailIndex(modalIndex);
      setEnlargedMediaData(carouselItem);
    },
    [imageData, threeSixtyImageThumbnails, videoThumbnails],
  );

  const handleClose = () => {
    setPinchToZoomImagesInitialized(false);
    initializedImages.current = 0;
    setModalOpen(false);
  };

  const handleOnKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (
      event.keyCode === eventConstants.enterKeyCode &&
      event.key === eventConstants.enterKeyType
    ) {
      return setModalOpen(true);
    }
  }, []);

  const handleOpenCarouselModal = useCallback(() => {
    setModalOpen(true);

    if (aprimoVideoTag) {
      aprimoVideoTag.pause();
    }
  }, [aprimoVideoTag]);

  const handleThumbnailClick = useCallback(
    (item: CarouselItem) => {
      const index = findThumbnailIndex(allThumbnails, item.thumbnail);

      trackImageInteractionPDP(`PDP|${index + 1}`, `${index + 1}`);
      const callback = isDesktop
        ? handleOpenCarouselModal
        : (thumbnail: CarouselItem) => {
            if (thumbnail.type === 'spin360') {
              handleOpenCarouselModal();
            }
          };
      callback(item);
    },
    [allThumbnails, isDesktop, handleOpenCarouselModal],
  );

  const handleSeeMoreClick = useCallback(() => {
    handleOpenCarouselModal();
    trackImageInteractionPDP('See More Images-PDP');
  }, [handleOpenCarouselModal]);

  const handleVehicleSpecificImagesBannerClick = () => {
    setModalTab('Port Locator');
    handleOpenCarouselModal();
    clickTrack({
      customLinkName: 'Vehicle Specific Image View',
      eventType: 'vehicleSpecificImageView',
      pageName: `az:${pageName}-${partNumber}`,
      pageType: pageTypes.pdp,
      siteSection: 'catalog',
      productIdList: [productDetailsData?.product?.itemId ?? ''],
    });
  };

  const handleVehicleInstallGuideVideosBannerClick = useCallback(() => {
    setModalTab('Installation Guide');
    handleOpenCarouselModal();
    clickTrack({
      customLinkName: `Install This ${partTypeName} Banner Click - ${pageTypes.pdp}`,
      eventType: `installThis${partTypeName}BannerClick`,
      pageName: `az:${pageName}-${partNumber}`,
      pageType: pageTypes.pdp,
      siteSection: 'catalog',
      productIdList: [productDetailsData?.product?.itemId ?? ''],
    });
  }, [handleOpenCarouselModal, pageName, partNumber, partTypeName, productDetailsData]);

  const shouldDisplaySylvaniaInstallGuideBanner =
    Boolean(props.data.vehicleFitFlag) && props.data.lineCode === 'SYL' && installationGuideEnabled;

  const shouldDisplayVehicleSpecificImagesBanner =
    isVehicleSpecificImagesBannerEnabled &&
    locale === 'en-US' &&
    Boolean(props.data.vehicleFitFlag) &&
    (productVehicleImage?.images?.length ?? 0) > 0;

  useDebouncedResizeObserver(
    productImageRefObject,
    useCallback((width: number) => {
      setEnlargedMediaContainerWidth(width);
    }, []),
  );

  if (allThumbnails.length > 0) {
    return (
      <div className={styles.productCarouselContainer} id="productCarouselContainer">
        {modalOpen ? (
          <Suspense fallback={<ProgressModal noScroll />}>
            <LazyProductImageModal
              open={modalOpen}
              imageCaption={imageCaption}
              imageData={imageData}
              videoThumbnails={videoThumbnails}
              threeSixtyImageThumbnails={threeSixtyImageThumbnails}
              selectedModalThumbnailIndex={selectedModalThumbnailIndex}
              showExpandedImageModal={showExpandedImageModal}
              data={props.data}
              handleModalTabChange={handleModalTabChange}
              modalTab={modalTab}
              onClose={handleClose}
              pinchToZoomImagesInitialized={pinchToZoomImagesInitialized}
              onPinchToZoomImageInit={onPinchToZoomImageInit}
              threeSixtySkuItem={
                threeSixtyImageData?.thumbnail
                  ? { thumbnail: threeSixtyImageData.thumbnail }
                  : undefined
              }
              threeSixtyUrls={urls}
              setSelectedPDPThumbnailIndex={setSelectedPDPThumbnailIndex}
              videoThumbnailsExist={videoThumbnailsExist}
              installationGuideExist={installationGuideEnabled}
              productVehicleImage={productVehicleImage}
              location={location ?? ''}
              vehicleInstallGuideEnabled={vehicleInstallGuideEnabled}
              vehicleInstallationGuideVideos={vehicleInstallationGuideVideos}
            />
          </Suspense>
        ) : null}
        {!isMobile ? (
          <View direction="column">
            <View className={styles.desktopMedia} direction="column">
              {props.data.configurableSkuFlag && (
                <View className={styles.configurableSkuBadgeContainer}>
                  <ConfigurableSkuBadge />
                </View>
              )}
              <LazyEnlargedMediaComponent
                enlargedMedia={enlargedMedia}
                handleOnKeyDown={handleOnKeyDown}
                onClick={handleOpenCarouselModal}
                caption={imageCaption}
                setAprimoVideoTag={setAprimoVideoTag}
                threeSixtyUrls={urls}
                containerRef={productImageRefCallback}
              />
              <View padding={{ s: 0, m: [5, 0, 0, 0], l: [5, 0, 0, 0] }}>
                <ThumbnailComponentV2
                  imgTitle={itemDescription}
                  onImageChange={showExpandedImagePDP}
                  selectedThumbnailIndex={selectedPDPThumbnailIndex}
                  handleSeeMoreClick={handleSeeMoreClick}
                  handleThumbnailClick={handleThumbnailClick}
                  loading={gcpProductsVideosResult.isFetching}
                  items={visibleThumbnails}
                  hoverEnabled
                  maxNumberOfThumbnails={maxVisibleThumbnails}
                  enableScroll={false}
                  forceShowSeeMore={showSeeMore}
                />
              </View>
            </View>
          </View>
        ) : null}
        <div className={styles.mobileCarousel}>
          {props.data.configurableSkuFlag && <ConfigurableSkuBadge />}
          <CarouselComponentV2
            imgTitle={itemDescription}
            items={allThumbnails}
            showMediumImageBeforeInteraction
            carouselCaption={imageCaption}
            onImageChange={showExpandedImagePDP}
            handleclick={handleOpenCarouselModal}
            selectedThumbnailIndex={selectedPDPThumbnailIndex}
            controlled
            useMagnify={false}
            classes={{ customCarouselContainer: styles.customCarouselContainer }}
          />
          <ThumbnailComponentV2
            imgTitle={itemDescription}
            onImageChange={showExpandedImagePDP}
            selectedThumbnailIndex={selectedPDPThumbnailIndex}
            handleThumbnailClick={handleThumbnailClick}
            items={allThumbnails}
            maxNumberOfThumbnails={allThumbnails.length}
            enableScroll={true}
            forceShowSeeMore={false}
          />
        </div>
        {shouldDisplaySylvaniaInstallGuideBanner && (
          <YMMESpecificInstallVideoBannerV2
            data={sylvaniaData}
            onClick={() => {
              setModalTab('Installation Guide');
              handleOpenCarouselModal();
            }}
          />
        )}
        {shouldDisplayVehicleSpecificImagesBanner && (
          <VehicleSpecificImagesBanner onClick={handleVehicleSpecificImagesBannerClick} />
        )}
        {vehicleInstallGuideEnabled && (
          <VehicleInstallGuideVideoModalBanner
            onClick={handleVehicleInstallGuideVideosBannerClick}
          />
        )}
      </div>
    );
  }

  return <div className={styles.noProdMsg}>{labels.label_CategoryPage_body_ImageNotAvailable}</div>;
};
